import {useEffect, useRef, useState} from 'react'
import {NavLink, Outlet, Link, useLocation} from 'react-router-dom'
import {
  LayoutDashboard,
  Timer,
  RefreshCw,
  Grid3x3,
  ArrowLeft,
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  X,
  Clock3,
  ChevronRight,
} from 'lucide-react'
import {getUserData} from '../../../modules/auth'
import {getFoto} from '../api'
import {useEscopo} from '../ServiceLevelContext'
import {hasServiceLevelPermission} from '../permissions'
import {fmtDataHoraFoto} from '../utils/format'
import {LogoImagem} from '../components/Logo'
import useDialog from '../components/useDialog'
import useScreenActivity from '../useScreenActivity'
import '../sl.css'

const activeCls = ({isActive}: {isActive: boolean}) => (isActive ? 'active' : '')
const CHAVE_COMPACTA = 'sl_sidebar_compacta'

export default function ServiceLevelLayout() {
  const [foto, setFoto] = useState<string | null>(null)
  const [fotoPerfilComErro, setFotoPerfilComErro] = useState<string | null>(null)
  const [compacta, setCompacta] = useState(() => {
    try {
      return localStorage.getItem(CHAVE_COMPACTA) === '1'
    } catch {
      return false
    }
  })
  const [menuAberto, setMenuAberto] = useState(false)
  const menuRef = useRef<HTMLElement>(null)
  const location = useLocation()
  const escopo = useEscopo()
  useScreenActivity(escopo)
  const isAdmin = !!escopo?.is_admin
  const podeSincronizar = hasServiceLevelPermission(escopo, 'sync')
  const podeSlaSeller = hasServiceLevelPermission(escopo, 'sla_sellers')
  const podeRegioesSlas = hasServiceLevelPermission(escopo, 'regioes_slas')
  const nome = escopo?.usuario.nome || 'Usuário'
  const perfil = getUserData()
  const fotoPerfil = typeof perfil?.src === 'string' ? perfil.src.trim() : ''
  const iniciais = nome
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((parte) => parte[0])
    .join('')
    .toUpperCase()
  const pagina = location.pathname.includes('/admin/regioes-slas')
    ? 'Regiões & SLAs'
    : location.pathname.includes('/admin/sla-sellers')
    ? 'SLA por Seller'
    : location.pathname.includes('/admin')
    ? 'Sincronização'
    : location.pathname.includes('/pedidos/')
    ? 'Detalhe do pedido'
    : location.pathname.includes('/sellers/')
    ? 'Detalhe do seller'
    : location.pathname.includes('/industrias/')
    ? 'Detalhe da indústria'
    : 'Dashboard'

  useDialog(menuAberto, menuRef, () => setMenuAberto(false))

  useEffect(() => {
    let ativo = true
    getFoto()
      .then((r) => {
        if (ativo) setFoto(r.ts_foto)
      })
      .catch(() => {})
    return () => {
      ativo = false
    }
  }, [])

  useEffect(() => {
    setMenuAberto(false)
  }, [location.pathname])

  useEffect(() => {
    const media = window.matchMedia('(min-width: 901px)')
    const fechar = () => {
      if (media.matches) setMenuAberto(false)
    }
    media.addEventListener('change', fechar)
    return () => media.removeEventListener('change', fechar)
  }, [])

  useEffect(() => {
    const anterior = document.title
    document.title = `${pagina} · Service Level | Insight Experience`
    return () => {
      document.title = anterior
    }
  }, [pagina])

  const alternar = () => {
    setCompacta((v) => {
      try {
        localStorage.setItem(CHAVE_COMPACTA, v ? '0' : '1')
      } catch {
        /* preferência opcional */
      }
      return !v
    })
  }

  const item = (to: string, Icone: typeof LayoutDashboard, rotulo: string, end = false) => (
    <NavLink
      to={to}
      end={end}
      className={activeCls}
      title={compacta && !menuAberto ? rotulo : undefined}
      onClick={() => setMenuAberto(false)}
    >
      <Icone size={20} aria-hidden='true' /> <span className='rotulo'>{rotulo}</span>
    </NavLink>
  )

  const avatar =
    fotoPerfil && fotoPerfil !== fotoPerfilComErro ? (
      <img src={fotoPerfil} alt='' onError={() => setFotoPerfilComErro(fotoPerfil)} />
    ) : (
      iniciais
    )

  return (
    <div className='sl-root'>
      <a className='skip-link' href='#sl-main'>
        Pular para o conteúdo
      </a>
      <div className={`app ${compacta ? 'compacta' : ''} ${menuAberto ? 'menu-aberto' : ''}`}>
        {menuAberto && (
          <div
            className='sidebar-backdrop'
            aria-hidden='true'
            onClick={() => setMenuAberto(false)}
          />
        )}
        <aside
          id='sl-navigation'
          ref={menuRef}
          className='sidebar'
          role={menuAberto ? 'dialog' : undefined}
          aria-modal={menuAberto || undefined}
          aria-label='Menu do Service Level'
          tabIndex={-1}
        >
          <div className='logo'>
            <Link
              to='/service-level/dashboard'
              className='marca'
              aria-label='Compra Agora — Dashboard do Service Level'
              onClick={() => setMenuAberto(false)}
            >
              <LogoImagem height={46} />
              <small>INSIGHT EXPERIENCE</small>
            </Link>
            <button
              type='button'
              className='sidebar-toggle desktop-only'
              onClick={alternar}
              title={compacta ? 'Expandir menu' : 'Recolher menu'}
              aria-label={compacta ? 'Expandir menu' : 'Recolher menu'}
              aria-expanded={!compacta}
            >
              {compacta ? <PanelLeftOpen size={19} /> : <PanelLeftClose size={19} />}
            </button>
            <button
              type='button'
              className='sidebar-toggle mobile-only'
              onClick={() => setMenuAberto(false)}
              aria-label='Fechar menu'
            >
              <X size={22} />
            </button>
          </div>
          <nav aria-label='Navegação principal'>
            <div className='section'>Acompanhamento</div>
            {item('/service-level/dashboard', LayoutDashboard, 'Dashboard')}
            {(podeSincronizar || podeSlaSeller || podeRegioesSlas) && (
              <>
                <div className='section'>Administração</div>
                {podeSincronizar && item('/service-level/admin', RefreshCw, 'Sincronização', true)}
                {podeSlaSeller && item('/service-level/admin/sla-sellers', Timer, 'SLA por Seller')}
                {podeRegioesSlas &&
                  item('/service-level/admin/regioes-slas', Grid3x3, 'Regiões & SLAs')}
              </>
            )}
          </nav>
          <div className='sl-voltar'>
            <Link to='/home' title='Voltar ao Insight Experience'>
              <ArrowLeft size={18} aria-hidden='true' />
              <span>Insight Experience</span>
            </Link>
          </div>
          <div className='sidebar-user' title={nome}>
            <span className='avatar' role='img' aria-label={nome}>
              {avatar}
            </span>
            <div className='sidebar-user-info'>
              <strong>{nome}</strong>
              <span>{isAdmin ? 'Administrador' : 'Acompanhamento de serviço'}</span>
            </div>
          </div>
          {foto && (
            <div className='foto-rodape'>
              <Clock3 size={12} aria-hidden='true' />
              Atualizado em {fmtDataHoraFoto(foto)}
            </div>
          )}
        </aside>
        <div className='sl-workspace'>
          <header className='topbar'>
            <div className='topbar-heading'>
              <button
                type='button'
                className='icon-button mobile-only'
                onClick={() => setMenuAberto(true)}
                aria-label='Abrir menu'
                aria-expanded={menuAberto}
                aria-controls='sl-navigation'
              >
                <Menu size={23} />
              </button>
              <Link to='/service-level/dashboard' className='topbar-brand'>
                <LogoImagem height={34} />
              </Link>
              <div className='topbar-breadcrumb desktop-only'>
                <span>Insight Experience</span>
                <ChevronRight size={14} aria-hidden='true' />
                <strong>Service Level</strong>
                <ChevronRight size={14} aria-hidden='true' />
                <span>{pagina}</span>
              </div>
            </div>
            <div className='topbar-actions'>
              <Link to='/home' className='sl-voltar-topo' aria-label='Voltar ao Insight Experience'>
                <ArrowLeft size={17} aria-hidden='true' />
                <span>Insight Experience</span>
              </Link>
              <span className='avatar desktop-only' title={nome} role='img' aria-label={nome}>
                {avatar}
              </span>
            </div>
          </header>
          <main id='sl-main' className='main' tabIndex={-1}>
            <Outlet />
          </main>
          <footer className='sl-footer'>
            <span>Service Level · Insight Experience</span>
            <span>Compra Agora</span>
          </footer>
        </div>
        <nav className='bottombar' aria-label='Navegação rápida'>
          <NavLink to='/service-level/dashboard' className={activeCls}>
            <LayoutDashboard size={21} aria-hidden='true' />
            <span>Dashboard</span>
          </NavLink>
          {podeSincronizar && (
            <NavLink to='/service-level/admin' end className={activeCls}>
              <RefreshCw size={21} aria-hidden='true' />
              <span>Sincronização</span>
            </NavLink>
          )}
          <button
            type='button'
            onClick={() => setMenuAberto(true)}
            aria-expanded={menuAberto}
            aria-controls='sl-navigation'
          >
            <Menu size={21} aria-hidden='true' />
            <span>Menu</span>
          </button>
          <Link to='/home'>
            <ArrowLeft size={21} aria-hidden='true' />
            <span>Insight</span>
          </Link>
        </nav>
      </div>
    </div>
  )
}
